import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../../hooks/useIsReseller";
import { getTimeAgo } from "../../../utils/getDate";
import api from "../../../utils/api";
import { connectSocket } from "../../../socket";

interface UnitDetail {
  id: string;
  unitId: string;
  name: string;
  status: "Reporting" | "Offline" | "Intermittent";
  lastReport: string;
  lastReportSeconds: number;
  location: string;
  downtime: string | null;
}

interface RiskAlert {
  id: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  title: string;
  description: string;
  timeAgo: string;
}

// Simplified device interface for what the API returns
interface DeviceFromAPI {
  id: string;
  fullId?: string;
  identifier?: string;
  companyId?: string;
  company_id?: string;
  vehicle?: {
    brand?: string;
    model?: string;
    plates?: string;
    plate?: string;
    name?: string;
  };
  lastPosition?: {
    lat?: number;
    lon?: number;
    speed?: number | null;
    timeStamp?: number;
  };
}

// Normalized device for internal use
interface NormalizedDevice {
  id: string;
  fullId: string;
  companyId: string;
  vehicleName: string;
  plates: string;
  lastReportSeconds: number;
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const FIVE_MINUTES_MS = 5 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;

const getReportingStatus = (lastReportSeconds: number): "Reporting" | "Offline" | "Intermittent" => {
  const lastReportMs = lastReportSeconds * 1000;
  const now = Date.now();
  const diff = now - lastReportMs;

  if (diff > TWENTY_FOUR_HOURS_MS || lastReportSeconds <= 0) {
    return "Offline";
  }
  if (diff > FIVE_MINUTES_MS) {
    return "Intermittent";
  }
  return "Reporting";
};

const getDowntimeFromSeconds = (lastReportSeconds: number): string | null => {
  const lastReportMs = lastReportSeconds * 1000;
  const now = Date.now();
  const diff = now - lastReportMs;

  if (diff <= FIVE_MINUTES_MS || lastReportSeconds <= 0) return null;

  const totalMinutes = Math.floor(diff / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const normalizeDevice = (device: DeviceFromAPI): NormalizedDevice => {
  const brand = device.vehicle?.brand || "";
  const model = device.vehicle?.model || "";
  const vehicleName = [brand, model].filter(Boolean).join(" ") || device.vehicle?.name || "—";
  const plates = device.vehicle?.plates || device.vehicle?.plate || "";

  // Convert timeStamp from ms to seconds
  let lastReportSeconds = 0;
  if (device.lastPosition?.timeStamp) {
    const ts = device.lastPosition.timeStamp;
    lastReportSeconds = ts > 9999999999 ? Math.floor(ts / 1000) : ts;
  }

  return {
    id: device.id,
    fullId: device.fullId || device.identifier || device.id,
    companyId: device.companyId || device.company_id || "",
    vehicleName,
    plates,
    lastReportSeconds,
  };
};

export default function EntregaServicioPlaceholder() {
  const { t } = useTranslation("entregaServicio");
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);

  // Company selection for resellers
  const companyId = useCompaniesStore((state) => state.companyId);
  const isReseller = (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;

  // Local state for fetched devices
  const [devices, setDevices] = useState<NormalizedDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Determine the target company ID for the API call
  const targetCompanyId = companyId;

  // Fetch devices from API, then enrich with actual last position timestamps
  const fetchDevices = useCallback(async (companyIdForFetch: string) => {
    setIsLoading(true);
    setFetchError(null);
    try {
      // Step 1: Get device list with metadata
      const listResponse = await api.get("/deviceList", {
        params: { limit: 200, page: 1 },
        headers: { "x-company-id": companyIdForFetch },
      });
      const rawData = listResponse.data.data || [];
      console.log("[EntregaServicio] Step 1 - deviceList raw sample:", rawData[0]);
      console.log("[EntregaServicio] Step 1 - Total devices from API:", rawData.length);

      const data: DeviceFromAPI[] = rawData;
      const normalized = data.map((d) => normalizeDevice(d));

      if (normalized.length === 0) {
        setDevices([]);
        return;
      }

      // Step 2: Get last positions — try socket first, fall back to REST
      const allIds = normalized.map((d) => d.id);
      const positionMap: Record<string, number> = {};

      // Strategy A: WebSocket (works on production where socket server is available)
      try {
        const socketInstance = connectSocket();

        if (!socketInstance.connected) {
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error("Socket connect timeout")), 3000);
            socketInstance.once("connect", () => { clearTimeout(timeout); resolve(); });
            socketInstance.once("connect_error", () => { clearTimeout(timeout); reject(new Error("Socket connect error")); });
          });
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const collected = await new Promise<Record<string, number>>((resolve) => {
          const results: Record<string, number> = {};
          let received = 0;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const onLastPosition = (position: any) => {
            const point = position?.point;
            const deviceId: string | null = position?.device_id ?? point?.device_id ?? null;
            if (!deviceId || !allIds.includes(deviceId)) return;

            const ts: number | undefined = point?.timeStamp;
            if (ts) {
              results[deviceId] = ts > 9999999999 ? Math.floor(ts / 1000) : ts;
            }
            received++;
            if (received >= allIds.length) {
              clearTimeout(timer);
              socketInstance.off("last_position", onLastPosition);
              resolve(results);
            }
          };

          socketInstance.on("last_position", onLastPosition);

          // Emit requests
          allIds.forEach((id) => {
            socketInstance.emit("get:last_position", { deviceId: id, withAddress: false, requestId: id });
          });

          const timer = setTimeout(() => {
            socketInstance.off("last_position", onLastPosition as never);
            resolve(results);
          }, 10000);
        });

        Object.assign(positionMap, collected);
        console.log("[EntregaServicio] Socket positions matched:", Object.keys(positionMap).length, "of", allIds.length);
      } catch (socketErr) {
        console.warn("[EntregaServicio] Socket failed, trying REST fallback:", socketErr);
      }

      // Strategy B: REST fallback (works on local dev / when socket is unavailable)
      if (Object.keys(positionMap).length === 0) {
        try {
          const CONCURRENT_LIMIT = 10;
          for (let i = 0; i < allIds.length; i += CONCURRENT_LIMIT) {
            const chunk = allIds.slice(i, i + CONCURRENT_LIMIT);
            const results = await Promise.allSettled(
              chunk.map((deviceId) =>
                api.get("/deviceLastPosition", { params: { id: deviceId } })
                  .then((res) => ({ deviceId, data: res.data.data }))
              )
            );
            for (const result of results) {
              if (result.status === "fulfilled" && result.value.data?.timeStamp) {
                const ts = result.value.data.timeStamp;
                positionMap[result.value.deviceId] = ts > 9999999999 ? Math.floor(ts / 1000) : ts;
              }
            }
          }
          console.log("[EntregaServicio] REST positions matched:", Object.keys(positionMap).length, "of", allIds.length);
        } catch (restErr) {
          console.warn("[EntregaServicio] REST fallback also failed:", restErr);
        }
      }

      console.log("[EntregaServicio] Total positions matched:", Object.keys(positionMap).length, "of", allIds.length);

      // Step 3: Merge position timestamps into normalized devices
      const enriched = normalized.map((device) => ({
        ...device,
        lastReportSeconds: positionMap[device.id] ?? device.lastReportSeconds,
      }));

      setDevices(enriched);
    } catch (err) {
      console.error("[EntregaServicio] Error fetching devices:", err);
      setFetchError("Error al cargar dispositivos");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch when target company changes
  useEffect(() => {
    if (!targetCompanyId) {
      setDevices([]);
      setIsLoading(false);
      return;
    }
    fetchDevices(targetCompanyId);
  }, [targetCompanyId, fetchDevices]);

  // Compute reporting stats from fetched devices
  const stats = useMemo(() => {
    const total = devices.length;
    let reporting = 0;
    let intermittent = 0;
    let offline = 0;
    let droppedLast24h = 0;

    const now = Date.now();

    for (const device of devices) {
      const status = getReportingStatus(device.lastReportSeconds);
      switch (status) {
        case "Reporting":
          reporting++;
          break;
        case "Intermittent":
          intermittent++;
          break;
        case "Offline":
          offline++;
          break;
      }

      const lastReportMs = device.lastReportSeconds * 1000;
      const diff = now - lastReportMs;
      if (diff > FIVE_MINUTES_MS && diff <= TWENTY_FOUR_HOURS_MS) {
        droppedLast24h++;
      }
    }

    const reportingAndIntermittent = reporting + intermittent;
    const percentage = total > 0 ? Math.round((reportingAndIntermittent / total) * 100) : 0;

    return {
      total,
      reporting: reportingAndIntermittent,
      notReporting: offline,
      activeUnits: reporting,
      intermittentUnits: intermittent,
      offlineUnits: offline,
      reportingPercentage: percentage,
      droppedLast24h,
    };
  }, [devices]);

  const { total: contracted, reporting, notReporting, activeUnits, intermittentUnits, offlineUnits, reportingPercentage, droppedLast24h: unitsDropped } = stats;

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "Reporting" | "Offline" | "Intermittent">("all");
  const [trendRange, setTrendRange] = useState<"7D" | "14D" | "30D">("30D");
  const [selectedTrendUnit, setSelectedTrendUnit] = useState<string>("all");

  // Build unit details from fetched devices
  const unitDetails: UnitDetail[] = useMemo(() => {
    return devices.map((device) => {
      const status = getReportingStatus(device.lastReportSeconds);
      const unitId = device.plates || device.fullId || device.id;

      return {
        id: device.id,
        unitId,
        name: device.vehicleName,
        status,
        lastReport: getTimeAgo(device.lastReportSeconds),
        lastReportSeconds: device.lastReportSeconds,
        location: "—",
        downtime: getDowntimeFromSeconds(device.lastReportSeconds),
      };
    }).sort((a, b) => {
      const order = { Offline: 0, Intermittent: 1, Reporting: 2 };
      return order[a.status] - order[b.status];
    });
  }, [devices]);

  const filteredUnits = useMemo(() => {
    return unitDetails.filter((unit) => {
      const matchesSearch = unit.unitId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        unit.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || unit.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [unitDetails, searchQuery, statusFilter]);

  const getStatusStyles = (status: UnitDetail["status"]) => {
    switch (status) {
      case "Reporting":
        return "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400";
      case "Offline":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
      case "Intermittent":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
    }
  };

  // Generate risk alerts dynamically from real data
  const riskAlerts: RiskAlert[] = useMemo(() => {
    const alerts: RiskAlert[] = [];

    const offlineOver24h = devices.filter((d) => {
      const diff = Date.now() - d.lastReportSeconds * 1000;
      return diff > TWENTY_FOUR_HOURS_MS && d.lastReportSeconds > 0;
    }).length;

    if (offlineOver24h > 0) {
      alerts.push({
        id: "offline-24h",
        priority: "HIGH",
        title: `${offlineOver24h} ${offlineOver24h === 1 ? t("alerts.unitOffline") || "unidad sin reportar" : t("alerts.unitsOffline") || "unidades sin reportar"} > 24h`,
        description: t("alerts.offlineDescription") || "Estas unidades no han reportado en más de 24 horas.",
        timeAgo: t("alerts.now") || "Ahora",
      });
    }

    const recentlyDropped = devices.filter((d) => {
      const diff = Date.now() - d.lastReportSeconds * 1000;
      return diff > ONE_HOUR_MS && diff <= TWENTY_FOUR_HOURS_MS;
    }).length;

    if (recentlyDropped > 0) {
      alerts.push({
        id: "recently-dropped",
        priority: "MEDIUM",
        title: `${recentlyDropped} ${recentlyDropped === 1 ? t("alerts.unitIntermittent") || "unidad intermitente" : t("alerts.unitsIntermittent") || "unidades intermitentes"}`,
        description: t("alerts.intermittentDescription") || "Reportaron en las últimas 24h pero no en la última hora.",
        timeAgo: t("alerts.now") || "Ahora",
      });
    }

    if (contracted > 0 && reportingPercentage < 95 && reportingPercentage > 0) {
      alerts.push({
        id: "sla-warning",
        priority: "LOW",
        title: t("alerts.slaWarning") || "Alerta de SLA",
        description: `${t("alerts.slaDescription") || "Nivel de servicio actual"}: ${reportingPercentage}% — ${t("alerts.slaThreshold") || "por debajo del umbral de 95%"}.`,
        timeAgo: t("alerts.now") || "Ahora",
      });
    }

    return alerts;
  }, [devices, contracted, reportingPercentage, t]);

  const getPriorityStyles = (priority: RiskAlert["priority"]) => {
    switch (priority) {
      case "HIGH":
        return {
          bg: "bg-red-100 dark:bg-red-900/30",
          text: "text-red-600 dark:text-red-400",
          border: "border-red-200 dark:border-red-800",
        };
      case "MEDIUM":
        return {
          bg: "bg-yellow-100 dark:bg-yellow-900/30",
          text: "text-yellow-600 dark:text-yellow-400",
          border: "border-yellow-200 dark:border-yellow-800",
        };
      case "LOW":
        return {
          bg: "bg-gray-100 dark:bg-gray-700",
          text: "text-gray-600 dark:text-gray-400",
          border: "border-gray-200 dark:border-gray-600",
        };
    }
  };

  // Show placeholder when reseller hasn't selected a company
  if (isReseller && !companyId) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
        <div className="flex flex-col items-center justify-center px-6 py-12">
          <div className={`w-20 h-20 rounded-full ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} flex items-center justify-center mb-6`}>
            <i className={`ri-building-2-line text-4xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
          </div>
          <h2 className={`text-xl font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"} text-center mb-3`}>
            {t("placeholder.selectCompanyTitle")}
          </h2>
          <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"} text-center max-w-md`}>
            {t("placeholder.selectCompanyHint")}
          </p>
        </div>
      </div>
    );
  }

  // Show error state if fetch failed
  if (fetchError) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
        <div className="flex flex-col items-center justify-center px-6 py-12">
          <div className={`w-20 h-20 rounded-full ${isDarkMode ? "bg-red-900/20" : "bg-red-50"} flex items-center justify-center mb-6`}>
            <i className={`ri-error-warning-line text-4xl text-red-500`}></i>
          </div>
          <h2 className={`text-xl font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"} text-center mb-3`}>
            {fetchError}
          </h2>
          <button
            onClick={() => targetCompanyId && fetchDevices(targetCompanyId)}
            className="mt-4 px-4 py-2 bg-primary-500 text-gray-900 rounded-lg text-sm font-medium hover:bg-primary-400 transition-colors"
          >
            {t("placeholder.retry") || "Reintentar"}
          </button>
        </div>
      </div>
    );
  }

  // Skeleton loading state that mirrors the dashboard layout
  if (isLoading) {
    const skeletonPulse = "animate-pulse";
    const skeletonBg = isDarkMode ? "bg-dark-card" : "bg-gray-200";

    return (
      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Health Overview Skeleton */}
          <div className={`lg:col-span-2 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className={`h-5 w-40 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                <div className={`h-3 w-64 ${skeletonBg} rounded mt-2 ${skeletonPulse}`}></div>
              </div>
              <div className={`h-7 w-36 ${skeletonBg} rounded-lg ${skeletonPulse}`}></div>
            </div>
            <div className="flex flex-col md:flex-row gap-8">
              {/* Circular Progress Skeleton */}
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className={`w-36 h-36 rounded-full ${skeletonBg} ${skeletonPulse}`}></div>
              </div>
              {/* Stats Skeleton */}
              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className={`h-4 w-36 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                    <div className={`h-4 w-24 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                  </div>
                  <div className={`h-3 w-full ${skeletonBg} rounded-full ${skeletonPulse}`}></div>
                  <div className="flex items-center gap-6 mt-2">
                    <div className={`h-3 w-20 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                    <div className={`h-3 w-24 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                    <div className={`h-3 w-20 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                  </div>
                </div>
                <div className={`h-px w-full ${skeletonBg}`}></div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  {[1, 2, 3].map((i) => (
                    <div key={i}>
                      <div className={`h-3 w-16 mx-auto ${skeletonBg} rounded ${skeletonPulse} mb-2`}></div>
                      <div className={`h-8 w-12 mx-auto ${skeletonBg} rounded ${skeletonPulse}`}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Risk Alerts Skeleton */}
          <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`h-5 w-28 ${skeletonBg} rounded ${skeletonPulse}`}></div>
              <div className={`h-6 w-16 ${skeletonBg} rounded-full ${skeletonPulse}`}></div>
            </div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className={`p-3 rounded-lg border ${themeClasses.cardBorder}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className={`h-5 w-14 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                    <div className={`h-3 w-12 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                  </div>
                  <div className={`h-4 w-40 ${skeletonBg} rounded ${skeletonPulse} mt-2`}></div>
                  <div className={`h-3 w-56 ${skeletonBg} rounded ${skeletonPulse} mt-1.5`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Unit Level Detail Skeleton */}
        <div className={`mt-4 sm:mt-6 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}>
          <div className="p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className={`h-5 w-36 ${skeletonBg} rounded ${skeletonPulse}`}></div>
            <div className="flex items-center gap-3">
              <div className={`h-9 w-40 ${skeletonBg} rounded-lg ${skeletonPulse}`}></div>
              <div className={`h-9 w-28 ${skeletonBg} rounded-lg ${skeletonPulse}`}></div>
            </div>
          </div>
          <div className="px-4 sm:px-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={`flex items-center gap-4 py-3 border-t ${themeClasses.cardBorder}`}>
                <div className={`h-4 w-4 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                <div className={`h-4 w-28 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                <div className={`h-5 w-20 ${skeletonBg} rounded-full ${skeletonPulse}`}></div>
                <div className={`h-4 w-24 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                <div className={`h-4 w-32 ${skeletonBg} rounded ${skeletonPulse} flex-1`}></div>
                <div className={`h-4 w-16 ${skeletonBg} rounded ${skeletonPulse}`}></div>
                <div className={`h-4 w-6 ${skeletonBg} rounded ${skeletonPulse}`}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Trend Graph Skeleton */}
        <div className={`mt-4 sm:mt-6 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className={`h-5 w-36 ${skeletonBg} rounded ${skeletonPulse}`}></div>
              <div className={`h-3 w-64 ${skeletonBg} rounded mt-2 ${skeletonPulse}`}></div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`h-9 w-36 ${skeletonBg} rounded-lg ${skeletonPulse}`}></div>
              <div className={`h-9 w-28 ${skeletonBg} rounded-lg ${skeletonPulse}`}></div>
            </div>
          </div>
          <div className={`h-64 w-full ${skeletonBg} rounded-lg ${skeletonPulse}`}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Health Overview Card */}
        <div className={`lg:col-span-2 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <div>
              <h2 className={`text-base sm:text-lg font-bold ${themeClasses.text}`}>Health Overview</h2>
              <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
                Real-time reporting status vs. contracted volume
              </p>
            </div>
            <div className="flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-red-50 dark:bg-red-900/20 rounded-lg w-fit">
              <i className="ri-arrow-down-line text-red-500 text-xs sm:text-sm"></i>
              <span className="text-red-500 text-xs sm:text-sm font-medium">
                {unitsDropped} dropped (24h)
              </span>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 sm:gap-8">
            {/* Circular Progress */}
            <div className="flex-shrink-0 flex flex-col items-center">
              <div className="relative w-36 h-36">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    fill="none"
                    stroke={isDarkMode ? "#3A3A3A" : "#e5e7eb"}
                    strokeWidth="12"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${reportingPercentage * 2.64} 264`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-2xl sm:text-3xl font-bold ${themeClasses.text}`}>{reportingPercentage}%</span>
                  <span className={`text-xs ${themeClasses.textSecondary} uppercase tracking-wide`}>Reporting</span>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div className="flex-1 space-y-4">
              {/* Contracted Coverage Bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${themeClasses.text}`}>Contracted Coverage</span>
                  <span className={`text-sm font-semibold ${themeClasses.text}`}>{reporting} / {contracted} Units</span>
                </div>
                <div className={`h-3 rounded-full overflow-hidden flex ${isDarkMode ? "bg-dark-surface" : "bg-gray-100"}`}>
                  <div 
                    className="h-full bg-[#22c55e] rounded-l-full"
                    style={{ width: contracted > 0 ? `${(activeUnits / contracted) * 100}%` : "0%" }}
                  />
                  <div 
                    className="h-full bg-[#facc15]"
                    style={{ width: contracted > 0 ? `${(intermittentUnits / contracted) * 100}%` : "0%" }}
                  />
                  <div 
                    className="h-full bg-[#ef4444] rounded-r-full"
                    style={{ width: contracted > 0 ? `${(offlineUnits / contracted) * 100}%` : "0%" }}
                  />
                </div>
                <div className="flex items-center gap-6 mt-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#22c55e]"></div>
                    <span className={`text-xs ${themeClasses.textSecondary}`}>Active ({activeUnits})</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#facc15]"></div>
                    <span className={`text-xs ${themeClasses.textSecondary}`}>Intermittent ({intermittentUnits})</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ef4444]"></div>
                    <span className={`text-xs ${themeClasses.textSecondary}`}>Offline ({offlineUnits})</span>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Contracted</p>
                  <p className={`text-lg sm:text-2xl font-bold ${themeClasses.text}`}>{contracted}</p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Reporting</p>
                  <p className="text-lg sm:text-2xl font-bold text-[#22c55e]">{reporting}</p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Not Reporting</p>
                  <p className="text-lg sm:text-2xl font-bold text-[#ef4444]">{notReporting}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Alerts Card */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2">
              <i className="ri-alarm-warning-line text-amber-500"></i>
              <h2 className={`text-base sm:text-lg font-bold ${themeClasses.text}`}>Risk Alerts</h2>
            </div>
            <span className="px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-semibold rounded-full">
              {riskAlerts.length} Active
            </span>
          </div>

          <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
            {riskAlerts.map((alert) => {
              const styles = getPriorityStyles(alert.priority);
              return (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg border ${styles.border} ${isDarkMode ? "" : styles.bg.split(" ")[0]}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${styles.bg} ${styles.text}`}>
                      {alert.priority}
                    </span>
                    <span className={`text-xs ${themeClasses.textSecondary}`}>{alert.timeAgo}</span>
                  </div>
                  <h3 className={`text-sm font-semibold ${themeClasses.text} mt-2`}>{alert.title}</h3>
                  <p className={`text-xs ${themeClasses.textSecondary} mt-0.5`}>{alert.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Unit Level Detail Table */}
      <div className={`mt-4 sm:mt-6 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}>
        {/* Header */}
        <div className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className={`text-base sm:text-lg font-bold ${themeClasses.text}`}>Unit Level Detail</h2>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {/* Search */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"} flex-1 sm:flex-initial sm:w-48`}>
              <i className={`ri-search-line ${themeClasses.textSecondary}`}></i>
              <input
                type="text"
                placeholder="Search Unit ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`bg-transparent outline-none text-sm ${themeClasses.text} placeholder:${themeClasses.textSecondary} w-full`}
              />
            </div>
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className={`px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"} ${themeClasses.text} text-sm outline-none cursor-pointer`}
            >
              <option value="all">All Statuses</option>
              <option value="Reporting">Reporting</option>
              <option value="Offline">Offline</option>
              <option value="Intermittent">Intermittent</option>
            </select>
            {/* Filter Button */}
            <button className={`p-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}>
              <i className="ri-filter-3-line"></i>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-t border-b ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"}`}>
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textSecondary} uppercase tracking-wider`}>Unit ID / Name</th>
                <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textSecondary} uppercase tracking-wider`}>Status</th>
                <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textSecondary} uppercase tracking-wider`}>Last Report</th>
                <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textSecondary} uppercase tracking-wider`}>Location</th>
                <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textSecondary} uppercase tracking-wider`}>Downtime</th>
                <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textSecondary} uppercase tracking-wider`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUnits.map((unit) => (
                <tr key={unit.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}>
                        <i className={`ri-truck-line ${themeClasses.textSecondary}`}></i>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${themeClasses.text}`}>{unit.unitId}</p>
                        <p className={`text-xs ${themeClasses.textSecondary}`}>{unit.name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusStyles(unit.status)}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        unit.status === "Reporting" ? "bg-primary-500" :
                        unit.status === "Offline" ? "bg-red-500" : "bg-yellow-500"
                      }`}></span>
                      {unit.status}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-sm ${themeClasses.textSecondary}`}>{unit.lastReport}</td>
                  <td className={`px-4 py-3 text-sm ${themeClasses.text}`}>{unit.location}</td>
                  <td className="px-4 py-3">
                    {unit.downtime ? (
                      <span className="text-sm text-red-500 font-medium">{unit.downtime}</span>
                    ) : (
                      <span className={`text-sm ${themeClasses.textSecondary}`}>-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button aria-label={t("general.moreOptions", { ns: "common" })} className={`p-1.5 rounded-lg ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}>
                      <i className="ri-more-2-fill"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={`px-4 py-3 flex items-center justify-between border-t ${themeClasses.cardBorder}`}>
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {filteredUnits.length} of {unitDetails.length} units
          </span>
          <div className="flex items-center gap-2">
            <button className={`px-3 py-1.5 text-sm rounded-lg border ${themeClasses.cardBorder} ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}>
              Previous
            </button>
            <button className={`px-3 py-1.5 text-sm rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} transition-colors`}>
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Reporting Trend Graph */}
      <div className={`mt-4 sm:mt-6 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div>
              <h2 className={`text-base sm:text-lg font-bold ${themeClasses.text}`}>Reporting Trend</h2>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {selectedTrendUnit === "all" 
                  ? `Active units vs. contracted baseline (${trendRange === "7D" ? "7" : trendRange === "14D" ? "14" : "30"} Days)`
                  : `Unit ${selectedTrendUnit} reporting history`
                }
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
            {/* Unit Selector - show only non-reporting units for quick access */}
            <select
              value={selectedTrendUnit}
              onChange={(e) => setSelectedTrendUnit(e.target.value)}
              className={`w-full sm:w-auto sm:max-w-[200px] px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"} ${themeClasses.text} text-sm outline-none cursor-pointer`}
            >
              <option value="all">{t("trends.allUnits")}</option>
              {unitDetails.filter((u) => u.status !== "Reporting").slice(0, 50).map((unit) => (
                <option key={unit.id} value={unit.unitId}>{unit.unitId} - {unit.name}</option>
              ))}
            </select>
            {/* Time Range Selector */}
            <div className={`flex items-center rounded-lg border ${themeClasses.cardBorder} overflow-hidden w-full sm:w-auto`}>
              {(["7D", "14D", "30D"] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => setTrendRange(range)}
                  className={`flex-1 sm:flex-initial px-3 py-1.5 text-sm font-medium transition-colors ${
                    trendRange === range
                      ? "bg-primary-500 text-gray-900"
                      : `${themeClasses.textSecondary} hover:${themeClasses.text} ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"}`
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="relative h-64">
          {/* Y Axis */}
          <div className="absolute left-0 top-0 h-full flex flex-col justify-between pr-3">
            {[520, 480, 440, 400].map((val) => (
              <span key={val} className={`text-xs ${themeClasses.textSecondary}`}>{val}</span>
            ))}
          </div>

          {/* Chart Area */}
          <div className="ml-10 h-full relative">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {/* Grid lines */}
              {[0, 33, 66, 100].map((y) => (
                <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="currentColor" strokeOpacity="0.1" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
              ))}
              
              {/* Contracted Baseline (dashed line at 500 units = ~17% from top if range is 400-520) */}
              <line 
                x1="0" 
                y1="17" 
                x2="100" 
                y2="17" 
                stroke={isDarkMode ? "#6b7280" : "#9ca3af"} 
                strokeWidth="1.5" 
                strokeDasharray="4 4"
                vectorEffect="non-scaling-stroke"
              />

              {/* Reporting Units Line */}
              <polyline
                points={selectedTrendUnit === "all" 
                  ? "0,33 10,30 20,28 30,30 40,35 50,40 60,42 70,38 80,35 90,32 100,30"
                  : "0,45 10,40 20,35 30,38 40,42 50,35 60,30 70,35 80,40 90,38 100,35"
                }
                fill="none"
                stroke="#22c55e"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            {/* X Axis labels */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-between transform translate-y-6">
              {trendRange === "7D" 
                ? ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"].map((day, i) => (
                    <span key={i} className={`text-xs ${themeClasses.textSecondary}`}>{day}</span>
                  ))
                : trendRange === "14D"
                ? ["Day 1", "Day 5", "Day 10", "Day 14"].map((day, i) => (
                    <span key={i} className={`text-xs ${themeClasses.textSecondary}`}>{day}</span>
                  ))
                : ["Day 1", "Day 5", "Day 10", "Day 15", "Day 20", "Day 25", "Day 30"].map((day, i) => (
                    <span key={i} className={`text-xs ${themeClasses.textSecondary}`}>{day}</span>
                  ))
              }
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-8 mt-10">
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-[#22c55e] rounded"></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>Reporting Units</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-6 h-0.5 ${isDarkMode ? "bg-gray-500" : "bg-gray-400"} rounded`} style={{ backgroundImage: "repeating-linear-gradient(90deg, currentColor, currentColor 4px, transparent 4px, transparent 8px)" }}></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>Contracted Baseline</span>
          </div>
        </div>
      </div>
    </div>
  );
}
